import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import { type ListBenchmarkImagesQuery, type ListBenchmarkImagesQueryVariables, pollWhilePending } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';

type BenchmarkImagesQuery = Query<ListBenchmarkImagesQuery, unknown, ListBenchmarkImagesQuery>;

/**
 * How the benchmark page learns that scoring finished, kept in this one file. The API cannot say when an uploaded
 * file lands in storage (image URLs are signed without checking it), so uploads are tracked on the client while
 * they run, not here. What the API does report is each image's scoring under a product category, when the listing
 * is asked for one: this re-reads the list on a fixed interval while any image's `scoringStatus` is `pending` and
 * stops once none is. Without a category nothing is pending and nothing polls. A scoring stuck pending stops counting
 * after the shared stale window. The scoring poll endpoint can replace this function; keep its signature.
 */
function benchmarkImagesRefetchInterval(variables: ListBenchmarkImagesQueryVariables) {
  return pollWhilePending<BenchmarkImagesQuery>(
    (query) =>
      (query.state.data?.images ?? [])
        .filter((image) => image.scoringStatus === 'pending')
        .map((image) => image.updatedAt),
    {
      onTimeout: (pendingCount) => {
        Sentry.captureException(new Error('Benchmark image scorings still pending after the stale window.'), {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Benchmark,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Benchmark].benchmarkImagePolling,
          },
          extra: { ...variables, pending_count: pendingCount },
        });
      },
    },
  );
}

export { benchmarkImagesRefetchInterval };
